'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  FilteredCounter,
  LifecycleFilter,
  ListPageFilterPanel,
} from '@terra/ui-design-system';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type {
  TraceAgentFilter,
  TraceResultFilter,
  TraceStatusFilter,
} from '../../../store/tab/tracesTabScopedFilterStore';
import { CreatedSinceFilterPill } from '../../common/CreatedSinceFilterPill';
import { ExecutionsAgentFilter } from './ExecutionsAgentFilter';
import { ExecutionsResultFilter } from './ExecutionsResultFilter';
import { ExecutionsStatusFilter } from './ExecutionsStatusFilter';

type ExecutionsFilterPanelProps = {
  selection?: ReactNode;
  statusFilter: TraceStatusFilter[];
  onStatusFilterChange: (statusFilter: TraceStatusFilter[]) => void;
  resultFilter: TraceResultFilter[];
  onResultFilterChange: (resultFilter: TraceResultFilter[]) => void;
  agentFilter: TraceAgentFilter[];
  onAgentFilterChange: (agentFilter: TraceAgentFilter[]) => void;
  createdSince: Date | null;
  onCreatedSinceClear: () => void;
  isLifecycleEnabled: boolean;
  lifecycleState: LifecycleStateFilterDto;
  onLifecycleStateChange: (lifecycleState: LifecycleStateFilterDto) => void;
  hasActiveFilters: boolean;
  onClear: () => void;
  showFilteredCounter: boolean;
  filteredCount: number;
  totalCount: number;
};

export function ExecutionsFilterPanel({
  selection,
  statusFilter,
  onStatusFilterChange,
  resultFilter,
  onResultFilterChange,
  agentFilter,
  onAgentFilterChange,
  createdSince,
  onCreatedSinceClear,
  isLifecycleEnabled,
  lifecycleState,
  onLifecycleStateChange,
  hasActiveFilters,
  onClear,
  showFilteredCounter,
  filteredCount,
  totalCount,
}: ExecutionsFilterPanelProps) {
  const tEntities = useTranslations('proxy.filteredCounter.entities');

  return (
    <ListPageFilterPanel
      selection={selection}
      isClearDisabled={!hasActiveFilters}
      onClear={onClear}
      trailing={
        showFilteredCounter ? (
          <FilteredCounter
            filteredCount={filteredCount}
            totalCount={totalCount}
            entityLabel={tEntities('executions')}
          />
        ) : null
      }
    >
      <ExecutionsStatusFilter
        value={statusFilter}
        onChange={onStatusFilterChange}
      />
      <ExecutionsResultFilter
        value={resultFilter}
        onChange={onResultFilterChange}
      />
      <ExecutionsAgentFilter
        value={agentFilter}
        onChange={onAgentFilterChange}
      />
      <CreatedSinceFilterPill
        createdSince={createdSince}
        onClear={onCreatedSinceClear}
      />
      {isLifecycleEnabled ? (
        <LifecycleFilter
          value={lifecycleState}
          onValueChange={onLifecycleStateChange}
        />
      ) : null}
    </ListPageFilterPanel>
  );
}
